/*
 * AccountSetup.tsx
 *
 * Page for setting up account by choosing a pin & 2 security questions.
 *
 * Created by Nick Perry on 9/2/22.
 *
 * dha-login
 *
 * Copyright © 2009-2022 United States Government as represented by
 * the Program Manager of the DHA: Web and Mobile Technology Program Management Office.
 * All Rights Reserved.
 *
 * Copyright © 2009-2022 Contributors. All Rights Reserved.
 *
 * THIS OPEN SOURCE AGREEMENT ("AGREEMENT") DEFINES THE RIGHTS OF USE,
 * REPRODUCTION, DISTRIBUTION, MODIFICATION AND REDISTRIBUTION OF CERTAIN
 * COMPUTER SOFTWARE ORIGINALLY RELEASED BY THE UNITED STATES GOVERNMENT
 * AS REPRESENTED BY THE GOVERNMENT AGENCY LISTED BELOW ("GOVERNMENT AGENCY").
 * THE UNITED STATES GOVERNMENT, AS REPRESENTED BY GOVERNMENT AGENCY, IS AN
 * INTENDED THIRD-PARTY BENEFICIARY OF ALL SUBSEQUENT DISTRIBUTIONS OR
 * REDISTRIBUTIONS OF THE SUBJECT SOFTWARE. ANYONE WHO USES, REPRODUCES,
 * DISTRIBUTES, MODIFIES OR REDISTRIBUTES THE SUBJECT SOFTWARE, AS DEFINED
 * HEREIN, OR ANY PART THEREOF, IS, BY THAT ACTION, ACCEPTING IN FULL THE
 * RESPONSIBILITIES AND OBLIGATIONS CONTAINED IN THIS AGREEMENT.
 *
 * Government Agency: DHA: Web and Mobile Technology Program Management Office
 * Government Agency Original Software Designation: dha-login
 * Government Agency Original Software Title: dha-login
 * User Registration Requested. Please send email
 * with your contact information to: robert.a.kayl.civ@mail.mil
 * Government Agency Point of Contact for Original Software - Program Manager: robert.a.kayl.civ@mail.mil
 */

import { useContext } from 'react';

import { Dialog } from '@headlessui/react';
import { encrypt } from 'dha-encryption';
import { Formik } from 'formik';
import { useAtomValue } from 'jotai';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import Select from './LoginComponents/Select';
import { themeAtom } from '../../atoms/themeAtom';
import { CustomInput } from '../../components/CustomInput';
import { AuthContext } from '../../providers/AuthProvider';
import { LoginDatabaseContext } from '../../providers/LoginDatabaseProvider';
import { setCreated } from '../../slices/accountSlice';

const AccountSetup = () => {
  const theme = useAtomValue(themeAtom);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { DHA_Key, database } = useContext(LoginDatabaseContext);
  const { setUsername } = useContext(AuthContext);
  /* eslint-disable */
  const validationSchema = yup.object({
    username: yup
      .string()
      .required('Please enter a username')
      .max(12, 'username must be 12 characters or less')
      .trim(),
    pin: yup
      .string()
      .required('Enter a valid pin')
      .min(6, 'Pin must be at least 6 characters')
      .matches(new RegExp('^\\d+$'), 'Pin must only contain numbers')
      .trim(),
    question1: yup.string().required('Please enter a security question').trim(),
    answer1: yup.string().required('Please enter an answer to your security question').trim(),
    question2: yup.string().required('Please enter a security question').trim(),
    answer2: yup.string().required('Please enter an answer to your security question').trim(),
  });
  /* eslint-enable */

  return (
    <Dialog
      open={true}
      as="div"
      className="relative z-10"
      onClose={(boolie) => {
        if (boolie) console.log(''); // this is the peak of programming & human ingenuity.
      }}
    >
      <div className={`fixed inset-0 ${theme.modalBackground}`} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <Dialog.Panel
            className={`h-full max-h-fit w-full overflow-hidden rounded-2xl ${theme.cardColor} p-6 text-center align-middle shadow-xl transition-all md:max-w-xl`}
          >
            <Dialog.Title as="h3" className={`text-lg font-medium leading-6 ${theme.textColor}`}>
              Please create an account.
            </Dialog.Title>
            <Formik
              initialValues={{
                username: '',
                pin: '',
                question1: '',
                answer1: '',
                question2: '',
                answer2: '',
              }}
              onSubmit={(enteredValues) => {
                // Save to database
                if (database !== null) {
                  // function asynchronously encrypts values & stores encrypted ArrayBuffer in indexDB
                  const createAccount = async () => {
                    if (DHA_Key === undefined) {
                      console.log('Dha_Key is undefined?');
                      return;
                    }

                    try {
                      const encryptedUser = await encrypt(JSON.stringify(enteredValues), DHA_Key);
                      await database.userProfile.put(
                        {
                          userProfile: encryptedUser,
                        },
                        'primaryUser'
                      );

                      dispatch(setCreated(true));
                      setUsername(enteredValues.username);
                      // force refresh to immediately affirm that the user knows & can correctly use their PIN to login.
                      navigate('/');
                    } catch (err) {
                      console.log(err);
                    }
                  };
                  createAccount().catch((e) => console.error(e));
                }
              }}
              validationSchema={validationSchema}
            >
              {(formikProps) => (
                <form onSubmit={formikProps.handleSubmit}>
                  <div id="form-area" className="">
                    {/* username input */}
                    <div className="mt-8 flex flex-col items-center">
                      <label className={`w-full ${theme.textColor}`}>
                        Enter your desired username:
                        <CustomInput
                          type="text"
                          name="username"
                          value={formikProps.values.username}
                          isError={!!formikProps.errors.username}
                          isTouched={formikProps.touched.username}
                          onChange={formikProps.handleChange}
                          maxLength={12}
                          placeholder="enter username"
                        />
                      </label>

                      {formikProps.errors.username && formikProps.touched.username ? (
                        <div className="-mb-5 text-base font-medium text-rose-500" id="feedback">
                          {formikProps.errors.username}
                        </div>
                      ) : null}
                    </div>

                    {/* pin input */}
                    <div className="mt-8 flex flex-col items-center">
                      <label className={`w-full ${theme.textColor}`}>
                        Enter 6-digit PIN:
                        <CustomInput
                          type="password"
                          name="pin"
                          value={formikProps.values.pin}
                          isError={!!formikProps.errors.pin}
                          isTouched={formikProps.touched.pin}
                          onChange={formikProps.handleChange}
                          maxLength={6}
                          placeholder="enter PIN"
                        />
                      </label>

                      {formikProps.errors.pin && formikProps.touched.pin ? (
                        <div className="-mb-5 text-base font-medium text-rose-500" id="feedback">
                          {formikProps.errors.pin}
                        </div>
                      ) : null}
                    </div>

                    {/* Security question 1 */}
                    <div className="mt-8">
                      {/* headlessUI Listbox component */}
                      <Select peerQuestion={formikProps.values.question2} name="question1" />

                      <CustomInput
                        type="text"
                        name="answer1"
                        value={formikProps.values.answer1}
                        isError={!!formikProps.errors.answer1}
                        isTouched={formikProps.touched.answer1}
                        onChange={formikProps.handleChange}
                        placeholder="answer here..."
                      />

                      {/* display input-validation error message */}
                      {(formikProps.touched.question1 || formikProps.touched.answer1) &&
                      (formikProps.errors.question1 || formikProps.errors.answer1) ? (
                        <div className="block text-base font-medium text-rose-500" id="feedback">
                          {formikProps.errors.question1 || formikProps.errors.answer1}
                        </div>
                      ) : null}
                    </div>

                    {/* Security question 2 */}
                    <div className="mt-8">
                      {/* headlessUI Listbox component */}
                      <Select peerQuestion={formikProps.values.question1} name="question2" />

                      <CustomInput
                        type="text"
                        name="answer2"
                        value={formikProps.values.answer2}
                        isError={!!formikProps.errors.answer2}
                        isTouched={formikProps.touched.answer2}
                        onChange={formikProps.handleChange}
                        placeholder="answer here..."
                      />

                      {/* display input-validation error message */}
                      {(formikProps.touched.question2 || formikProps.touched.answer2) &&
                      (formikProps.errors.question2 || formikProps.errors.answer2) ? (
                        <div className="block text-base font-medium text-rose-500" id="feedback">
                          {formikProps.errors.question2 || formikProps.errors.answer2}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="">
                    <button
                      type="submit"
                      className={`inline-flex justify-center rounded-md border border-transparent ${theme.buttonShadow} ${theme.buttonColor} ${theme.buttonTextColor} px-4 py-2 text-sm font-medium hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 mt-4`}
                    >
                      Create Account
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default AccountSetup;
